<template>
  <aw-form v-model="v$" @success="onDeliveryDataSaved">
    <template v-if="isModalMode">
      <div :class="$style.modalHeader">
        <span :class="$style.modalHeaderLocation">
          <lv-icon name="location-16" :size="16" :class="$style.modalHeaderLocationIcon" />
        </span>
        <div :class="$style.modalHeaderPointData">
          <div> {{ selectedPoint?.name }}</div>
          <div>
            {{
              (selectedPoint?.address?.postCode + ' ' + selectedPoint?.address?.city)
            }}
            <br>
            {{
              selectedPoint?.address?.streetName || ''
            }}
          </div>
        </div>
        <button
          type="button"
          :aria-label="$awt('aw.checkout.delivery_data_form.back_to_map')"
          class="buttonReset awFocusV"
          @click="backToMap"
        >
          <lv-icon name="arrow-right-16" :size="16" />
        </button>
      </div>
      <aw-separator />
    </template>

    <div :class="$style.inputList">
      <div
        v-for="{ key, field, error } in [
          ...(isGuestCheckout ? [EMAIL, CONFIRM_EMAIL] : []),
          isRev ? LAST_NAME : FIRST_NAME,
          isRev ? FIRST_NAME : LAST_NAME,
          CONTACT_PHONE,
          COMMENT
        ].map(key => ({ key, ...(validators[key] || { field: {}, error: {} }) }))"
        :key="key"
        :class="$style.inputListItem"
      >
        <label
          :for="`${componentId}_${key}`"
          :class="[$style.inputListItemLabel, {[$style.inputListItemLabelComment]: key === COMMENT}]"
          v-text="labels[key]"
        />

        <aw-textarea-version2
          v-if="key === COMMENT"
          :class="$style.inputListItemInput"
          v-bind="field"
          :widget-attrs="{ maxLength: TEXTAREA_MAX_LENGTH }"
          has-label
          :floating-label-text="floatingLabels[key]"
          need-countdown
        />
        <div
          v-else
          :class="[
            $style.inputListItemInput,
            {
              [$style.inputListItemInputMedium]: MEDIUM_FIELDS.includes(key),
              [$style.inputListItemInputLong]: LONG_FIELDS.includes(key),
              [$style.inputListItemInputPhone]: PHONE_FIELDS.includes(key),
            }]"
        >
          <aw-input-version2
            v-bind="field"
            :has-label="Boolean(floatingLabels[key])"
            :floating-label-text="floatingLabels[key] || ''"
            :widget-attrs="{ id: `${componentId}_${key}` }"
          >
            <template v-if="prefixes[key]" #prefix>
              {{ prefixes[key] }}
            </template>
          </aw-input-version2>
        </div>
        <div :class="$style.inputListItemHelp" v-text="helpTexts[key]" />
        <aw-minimal-error :class="$style.inputListItemError" v-bind="error" />
      </div>
    </div>
    <template v-if="!isDmModalMode">
      <aw-separator margin-bottom="checkout" />
      <aw-minimal-error :class="$style.inputListItemError" v-bind="{ id: mapErrorId, validator: v$[SELECTED_POINT] }" />
      <lv-button
        :type="v$.$invalid ? 'button' : 'submit'"
        :disabled="v$.$invalid"
        :loading="isLoading"
        styling="primary-dark"
        :shrink="!isButtonStretch"
        :stretch="isButtonStretch"
      >
        {{ $awt('aw.checkout.delivery_data_form.save_button') }}
      </lv-button>
    </template>
  </aw-form>
</template>

<script setup lang="ts">
  import { computed, onMounted, reactive, ref, toRefs, watch } from 'vue';
  import { useVuelidate } from '@vuelidate/core';
  import { helpers } from '@vuelidate/validators';
  import { awEmail, awRequired, awValidOptionalMobile, phonePrefix } from '~~/common/utils/form';
  import { useId, useNuxtApp, useRuntimeConfig } from 'nuxt/app';
  import AwForm from '~~/common/components/Form/AwForm.vue';
  import AwInputVersion2, { awInputV2Props } from '~~/common/components/Common/AwInputVersion2.vue';
  import AwMinimalError from '~~/common/components/Common/AwMinimalError.vue';
  import AwTextareaVersion2 from '~~/common/components/Common/AwTextareaVersion2.vue';
  import { LvButton, LvIcon } from '~~/common/components/loginet-vue-shop/index';
  import AwSeparator from '~~/common/components/Common/AwSeparator.vue';
  import { camelToSnakeCase } from '~~/common/utils/index';
  import { backendValidatorWithMessage, setErrorNotification, setValidationFromError } from '~~/common/utils/helper';
  import { useNameOrderConfig } from '~~/shop/app/config/NameOrderConfig';
  import { useUserStore } from '~~/common/stores/user';
  import { useCheckoutStore } from '~~/common/stores/checkout';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface.js';
  import { useDeliveryStore } from '~~/shop/app/stores/delivery.js';
  import { injectCheckout } from '~~/shop/app/config/checkoutInjection';
  import type { CurrentCheckout, CurrentCheckoutType, PickupPoint } from '~~/shop/app/config/checkoutInjection';
  import type { CheckoutData } from '~~/common/stores/checkout';
  import type { MappedDeliveryMethod } from '~~/shop/app/config/DeliveryMethodConfig';



  const TEXTAREA_MAX_LENGTH = 400;

  const props = withDefaults(defineProps<{
    formData?: NonNullable<CheckoutData['delivery']>['address'] | null;
    isGuestCheckout?: boolean;
    selectedDeliveryMethod: MappedDeliveryMethod;
    selectedPoint: PickupPoint | null;
    isModalMode?: boolean;
    isDmModalMode?: boolean;
  }>(), {
    formData: null,
    isGuestCheckout: true,
    isModalMode: false,
    isDmModalMode: false,
  });

  const emit = defineEmits(['pickup-point-data-saved', 'back-to-map', 'validation-status-changed']);

  const { isRev } = useNameOrderConfig();

  const SELECTED_POINT = 'selectedPoint';
  const EMAIL = 'email';
  const CONFIRM_EMAIL = 'confirmEmail';
  const FIRST_NAME = 'firstName';
  const LAST_NAME = 'lastName';
  const CONTACT_PHONE = 'contactPhone';
  const COMMENT = 'comment';
  const HIDE_ERRORS_DELAY = 6000;

  const componentId = useId();
  const mapErrorId = useId();
  const screenRange = computed(() => useUserInterfaceStore().mediaQueries);
  const nuxtApp = useNuxtApp();
  const {
    $awt,
    $logger,
  } = nuxtApp;
  const isLoading = ref(false);

  const currentCheckoutType = injectCheckout<CurrentCheckoutType>('currentCheckoutType');
  const currentCheckout = injectCheckout<CurrentCheckout>('currentCheckout');

  // Field groups the template renders differently. Typed as `string[]` so `includes` accepts the
  // loop's key without narrowing it first.
  const MEDIUM_FIELDS: string[] = [FIRST_NAME, LAST_NAME];
  const LONG_FIELDS: string[] = [EMAIL, CONFIRM_EMAIL];
  const PHONE_FIELDS: string[] = [CONTACT_PHONE];

  const models = {
    [SELECTED_POINT]: computed(() => props.selectedPoint),
    [FIRST_NAME]: ref(''),
    [LAST_NAME]: ref(''),
    [EMAIL]: ref(''),
    [CONFIRM_EMAIL]: ref(''),
    [CONTACT_PHONE]: ref(''),
    [COMMENT]: ref(''),
  };
  let clearFormTimeout: ReturnType<typeof setTimeout> | null = null;

  const isButtonStretch = computed(() => {
    return screenRange.value['mobile-max'];
  });

  const backendValidation = reactive({
    errors: {},
    propertyPaths: {
      [FIRST_NAME]: 'firstName',
      [LAST_NAME]: 'lastName',
      [CONTACT_PHONE]: 'contactPhone',
    },
  });

  const formVm = reactive({
    ...toRefs(models),
    backendValidation,
  });

  const config = useRuntimeConfig();
  const phoneValidator = (config.public.isDeliveryPhoneAllowed
    ? {
      required: awRequired(),
      mobile: awValidOptionalMobile(),
      backend: backendValidatorWithMessage(formVm, CONTACT_PHONE),
    }
    : {}
  );

  const v$ = useVuelidate({
    [SELECTED_POINT]: {
      isValidSp: helpers.withMessage(
        ({ $invalid }) => ($invalid ? $awt('aw.checkout.delivery_data_form.validator.please_select_a_point') : ''),
        (sp: PickupPoint | null) => {
          return Boolean(sp?.id);
        },
      ),
    },
    [FIRST_NAME]: {
      required: awRequired(),
      backend: backendValidatorWithMessage(formVm, FIRST_NAME),
      ucbAddressValidator (value: string) {
        return Boolean(useUserInterfaceStore().ucbAddressValidator(value));
      },
    },
    [LAST_NAME]: {
      required: awRequired(),
      backend: backendValidatorWithMessage(formVm, LAST_NAME),
      ucbAddressValidator (value: string) {
        return Boolean(useUserInterfaceStore().ucbAddressValidator(value));
      },
    },
    [EMAIL]: {
      required: awRequired({
        ifFun () {
          return props.isGuestCheckout;
        },
      }),
      email: awEmail(),
    },
    [CONFIRM_EMAIL]: {
      required: awRequired({
        ifFun () {
          return props.isGuestCheckout;
        },
      }),
      email: awEmail(),
    },
    [CONTACT_PHONE]: phoneValidator,
    [COMMENT]: {},
  }, models);

  const validators = computed(() => {
    type FieldProps = ReturnType<typeof awInputV2Props>;
    const result: Record<string, FieldProps> = {};
    ([
      FIRST_NAME,
      LAST_NAME,
      EMAIL,
      CONFIRM_EMAIL,
      CONTACT_PHONE,
      COMMENT,
    ] satisfies (keyof typeof models)[]).forEach(key => {
      result[key] = awInputV2Props({
        validator: v$.value,
        validatorName: key,
        model: models[key],
        id: `${componentId}_${key}_error`,
      });
    });
    return result;
  });

  const helpTexts = computed(() => {
    return Object.fromEntries(
      ([CONTACT_PHONE]
        .map((k) => {
          return [k, $awt(`aw.checkout.delivery_data_form.help.${camelToSnakeCase(k)}`)];
        })
      ),
    );
  });
  const labels = computed(() => {
    return Object.fromEntries(
      (Object.keys(validators.value)
        .map((k) => {
          return [k, $awt(`aw.checkout.delivery_data_form.label.${camelToSnakeCase(k)}`)];
        })
      ),
    );
  });
  const floatingLabels = computed(() => {
    return screenRange.value['desktop-small-min'] ? {} : labels.value;
  });
  const prefixes = computed<Record<string, string>>(() => {
    return {
      [CONTACT_PHONE]: phonePrefix.value ?? '',
    };
  });
  const submitData = computed(() => ({
    customer: {
      ...(currentCheckout.value?.customer),
      firstName: models[FIRST_NAME].value,
      lastName: models[LAST_NAME].value,
      ...(props.isGuestCheckout
        ? {
          phoneNumber: models[CONTACT_PHONE].value,
          email: models[EMAIL].value,
          confirmEmail: models[CONFIRM_EMAIL].value,
        }
        : {
          phoneNumber: models[CONTACT_PHONE].value,
          email: models[EMAIL].value,
        }
      ),
    },
    delivery: {
      deliveryMethod: props.selectedPoint?.type,
      nonFoodCourier: (props.selectedPoint?.nonFoodCourierId != undefined
        ? {
          id: props.selectedPoint.nonFoodCourierId,
        }
        : null
      ),
      //gls
      pickupPointData: {
        id: props.selectedPoint?.id,
        city: props.selectedPoint?.address?.city,
        name: props.selectedPoint?.name ?? '',
        phone: props.selectedPoint?.phoneNumber ?? '',
        openings: props.selectedPoint?.openingHours,
        postCode: props.selectedPoint?.address?.postCode,
        contact: props.selectedPoint?.contact ?? '',
        email: props.selectedPoint?.email ?? '',
        vendor_url: '',
      },
      address: {
        contactPhone: useUserInterfaceStore().str2Phone(models[CONTACT_PHONE].value.length ? [phonePrefix.value, models[CONTACT_PHONE].value].join('') : '', true).join(''),
        comment: models[COMMENT].value,
      },
    },
  }));

  onMounted(() => {
    initForm();
  });

  function initForm () {
    if (!props.isGuestCheckout) {
      initFormWithUserData();
    }
    const customer = currentCheckout.value?.customer;
    const delivery = currentCheckout.value?.delivery;
    for (const key of [FIRST_NAME, LAST_NAME, EMAIL, CONFIRM_EMAIL] as const) {
      const value = customer?.[key];
      if (value) {
        models[key].value = value;
      }
    }
    for (const key of [COMMENT, CONTACT_PHONE] as const) {
      const value = delivery?.address?.[key];
      if (value) {
        models[key].value = key === CONTACT_PHONE
          ? useUserInterfaceStore().str2Phone(value || '', true).slice(1).join('')
          : value;
      }
    }
  }

  function initFormWithUserData () {
    const personalInfo = useUserStore().data?.personalInfo;
    if (!personalInfo) {
      return;
    }
    models[FIRST_NAME].value = personalInfo.firstName ?? '';
    models[LAST_NAME].value = personalInfo.lastName ?? '';
    models[CONTACT_PHONE].value = useUserInterfaceStore().str2Phone(personalInfo.phoneNumber || '', true).slice(1).join('');
    models[EMAIL].value = personalInfo.email ?? '';
  }

  async function saveCheckoutDeliveryData () {
    let isSuccess = false;
    try {
      const {
        updateShopCheckout,
        fetchCheckout,
        fetchCheckoutAvailableDeliveryMethods,
      } = useCheckoutStore();
      const deliveryStore = useDeliveryStore();

      const result = await updateShopCheckout({
        data: submitData.value,
        type: currentCheckoutType.value,
      });
      await Promise.all([
        deliveryStore.fetchMethod(),
        fetchCheckout({ type: currentCheckoutType.value }),
        fetchCheckoutAvailableDeliveryMethods(currentCheckoutType.value),
      ]);
      isSuccess = result?.status;
      if (result?.isUserExistsError) {
        setErrorNotification({
          nuxtApp: useNuxtApp(),
          error: $awt('aw.checkout.delivery_data_form.validator.user_exists'),
        });
      }
      if (!result.status) {
        setBackendValidationError(result.error);
      }
    } catch (error) {
      $logger.error(error);
      isSuccess = false;
    }
    return isSuccess;
  }

  async function onDeliveryDataSaved () {
    const {
      startLoading,
      endLoading,
    } = useUserInterfaceStore();
    const loadingId = { id: `delivery-checkout-data-form-save-${props.formData?.id || 'new'}` };
    try {
      isLoading.value = true;
      startLoading(loadingId);
      if (await saveCheckoutDeliveryData()) {
        emit('pickup-point-data-saved');
      }
    } catch (err) {
      $logger.error(err);
    } finally {
      isLoading.value = false;
      endLoading(loadingId);
    }
  }

  function backToMap () {
    emit('back-to-map');
  }

  function setBackendValidationError (error: unknown) {
    setValidationFromError(formVm, error, nuxtApp);
  }

  watch(v$, (newVal) => {
    emit('validation-status-changed', !newVal.$invalid);
  });

  watch(backendValidation, (newValue) => {
    clearTimeout(clearFormTimeout ?? undefined);
    if (Object.keys(newValue.errors).length) {
      clearFormTimeout = setTimeout(() => {
        v$.value.$reset();
        backendValidation.errors = {};
      }, HIDE_ERRORS_DELAY);
    }
  }, { deep: true });

  defineExpose({
    validation: v$,
    submitData,
    setBackendValidationError,
  });
</script>

<style module lang="scss" rel="stylesheet/scss">

.inputList {
  @include font(null, 16px, 24px);
  display: flex;
  flex-direction: column;
  gap: 20px;

  &Accordion {
    gap: 12px;
    padding-top: 20px;

    @include desktop-small(min) {
      gap: 20px;
      padding: 24px 8px 12px;
    }

    .inputListItem {
      --deliveryDataFormLabelWidth: 176px;
    }
  }
}

.inputListItem {
  display: flex;
  flex-direction: column;
  gap: 0 40px;

  @include desktop-small(min) {
    --deliveryDataFormLabelWidth: 176px;
    display: grid;
    grid-template-columns: var(--deliveryDataFormLabelWidth) auto;
    grid-template-areas:
      "labelArea inputArea"
      ".         helpArea"
      ".         errorArea";
  }

  &:last-of-type {
    gap: 0 40px;
  }
}

.inputListItemLabel {
  grid-area: labelArea;
  align-self: center;

  @include tablet(max) {
    display: none;
  }
}

.inputListItemLabelComment {
  align-self: flex-start;
  padding: 14px 0;
}

.inputListItemInput {
  grid-area: inputArea;
}

.inputListItemInputShort {
  max-width: 104px;
}

.inputListItemInputMedium {
  max-width: 200px;
}

.inputListItemInputLong {
  max-width: 296px;
}

.inputListItemInputPhone {
  max-width: 152px;
}

.inputListItemHelp {
  grid-area: helpArea;
  font-style: italic;
  @include font(null, 14px, 20px);
  padding: 4px 16px 0;
  color: $color-text-secondary;

  &:empty {
    display: none;
  }
}

.inputListItemError {
  grid-area: errorArea;
}

.mobileFloatingLabel {
  padding-left: 54px;
}

.modalHeader {
  display: grid;
  grid-template-columns: 36px 1fr 16px;
  gap: 16px;
  align-items: center;

  &Location {
    width: 36px;
    height: 36px;
    padding: 9px;
    border: 1px solid $color-text-primary;
    border-radius: 12px;
    background: $color-background-4;

    &Icon {
      width: 16px;
      height: 16px;
      vertical-align: unset;
      color: $color-text-primary;
    }
  }

  &PointData {
    @include font($font-weight-bold-v2, 16px, 20px);
    text-align: left;
  }
}
</style>
